import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import HeroSection from './components/HeroSection';
import BenefitsSection from './components/BenefitsSection';
import FeatureShowcase from './components/FeatureShowcase';
import TrustSection from './components/TrustSection';
import PricingSection from './components/PricingSection';

const ListYourPropertyLanding = () => {
  const navigate = useNavigate();

  const handleStartListing = () => navigate('/create-listing');
  const handleAgentDashboard = () => navigate('/agent-dashboard');
  const handleQuickList = () => navigate('/create-listing?mode=quick');
  const handleBulkUpload = () => navigate('/agent-dashboard?tab=bulk');

  return (
    <div className="min-h-screen" style={{ background: '#050A14' }}>
      <Helmet>
        <title>List Your Property | AI-Powered Listings – ListingLens Australia & UAE</title>
        <meta name="description" content="Create professional property listings in minutes with AI-powered descriptions, automated market analysis, photo enhancement, and integrated lead capture. Serving Australia and UAE real estate markets." />
        <meta name="keywords" content="list property Australia, list property UAE, property listing platform, AI real estate listing, sell property Sydney Melbourne Brisbane, list property Dubai Abu Dhabi, real estate agent tools" />
        <link rel="canonical" href="https://listinglen3865.builtwithrocket.new/list-your-property" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://listinglen3865.builtwithrocket.new/list-your-property" />
        <meta property="og:title" content="List Your Property | AI-Powered Listings – ListingLens" />
        <meta property="og:description" content="Create professional property listings in minutes with AI-powered descriptions, automated market analysis, photo enhancement, and integrated lead capture. Serving Australia and UAE real estate markets." />
        <meta property="og:image" content="https://listinglen3865.builtwithrocket.new/assets/images/logo_LL-1772022913346.jpeg" />
        <meta property="og:site_name" content="ListingLens" />
        <meta property="og:locale" content="en_AU" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="List Your Property | AI-Powered Listings – ListingLens" />
        <meta name="twitter:description" content="Create professional property listings in minutes with AI-powered descriptions, automated market analysis, photo enhancement, and integrated lead capture." />
        <meta name="twitter:image" content="https://listinglen3865.builtwithrocket.new/assets/images/logo_LL-1772022913346.jpeg" />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "ListingLens Property Listing Service",
          "url": "https://listinglen3865.builtwithrocket.new/list-your-property",
          "provider": {
            "@type": "Organization",
            "name": "ListingLens",
            "url": "https://listinglen3865.builtwithrocket.new/"
          },
          "description": "AI-powered property listing creation service for Australian and UAE real estate markets. Features include AI-generated descriptions, automated market analysis, photo enhancement suggestions, and integrated lead capture.",
          "areaServed": ["Australia", "United Arab Emirates"],
          "serviceType": "Real Estate Listing Platform",
          "offers": {
            "@type": "Offer",
            "description": "Create professional property listings with AI assistance",
            "availability": "https://schema.org/InStock"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Listing Features",
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "AI-Powered Property Descriptions" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Automated Market Analysis" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Photo Enhancement Suggestions" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Integrated Lead Capture" } }
            ]
          }
        })}</script>
      </Helmet>

      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 20% 20%, rgba(0, 212, 255, 0.05) 0%, transparent 50%),
                           radial-gradient(circle at 80% 80%, rgba(123, 47, 255, 0.05) 0%, transparent 50%)`
        }} />
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(0, 212, 255, 0.03) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(0, 212, 255, 0.03) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      <nav className="fixed top-0 left-0 right-0 z-50" style={{
        background: 'rgba(5, 10, 20, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(0, 212, 255, 0.2)'
      }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img
                src="/assets/images/logo_LL-1772022913346.jpeg"
                alt="ListingLens logo"
                className="h-10 w-auto rounded-xl object-cover"
                style={{ boxShadow: '0 0 18px rgba(0, 212, 255, 0.5)', border: '2px solid rgba(0, 212, 255, 0.4)', maxWidth: '140px' }}
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleAgentDashboard}
                className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                style={{ color: '#6B9EC4', border: '1px solid rgba(0, 212, 255, 0.2)' }}
                onMouseEnter={e => { e.currentTarget.style.color = '#00D4FF'; e.currentTarget.style.borderColor = 'rgba(0,212,255,0.5)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#6B9EC4'; e.currentTarget.style.borderColor = 'rgba(0,212,255,0.2)'; }}
              >
                Agent Dashboard
              </button>
              <button
                onClick={handleStartListing}
                className="px-5 py-2 rounded-lg text-sm font-semibold transition-all"
                style={{
                  background: 'linear-gradient(135deg, #00D4FF, #7B2FFF)',
                  color: '#fff',
                  boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)'
                }}
              >
                Start Listing
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="relative z-10 pt-16">
        <HeroSection onStartListing={handleStartListing} onQuickList={handleQuickList} />
        <BenefitsSection />
        <FeatureShowcase onStartListing={handleStartListing} onQuickList={handleQuickList} onBulkUpload={handleBulkUpload} />
        <TrustSection />
        <PricingSection onStartListing={handleStartListing} />

        <footer className="py-10 px-4" style={{ borderTop: '1px solid rgba(0, 212, 255, 0.1)' }}>
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/assets/images/logo_LL-1772022913346.jpeg" alt="ListingLens" className="h-8 w-auto rounded-lg object-cover" style={{ maxWidth: '110px' }} />
            </div>
            <p className="text-sm" style={{ color: '#4A7A9B' }}>© 2026 ListingLens. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/')} className="text-sm" style={{ color: '#4A7A9B' }}>Home</button>
              <button onClick={() => navigate('/authentication')} className="text-sm" style={{ color: '#4A7A9B' }}>Sign In</button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ListYourPropertyLanding;
